/**
 * Feature flags (PE-2161, backend PE-2160 / osprey-api #101).
 *
 * Enums mirror osprey-api's own Prisma types verbatim — this module owns its
 * table (`prisma/migrations/20260829140000_add_feature_flags`), it is not a
 * proxy to a downstream service, so these are the whole contract.
 */

export const FeatureFlagType = {
  BOOLEAN: 'BOOLEAN',
  MULTIVARIATE: 'MULTIVARIATE',
} as const;
export type FeatureFlagType = (typeof FeatureFlagType)[keyof typeof FeatureFlagType];

export const FeatureFlagScope = {
  GLOBAL: 'GLOBAL',
  SOLUTION: 'SOLUTION',
  MERCHANT: 'MERCHANT',
} as const;
export type FeatureFlagScope = (typeof FeatureFlagScope)[keyof typeof FeatureFlagScope];

export const FeatureFlagRolloutType = {
  ALL: 'ALL',
  PERCENTAGE: 'PERCENTAGE',
  ALLOW_LIST: 'ALLOW_LIST',
} as const;
export type FeatureFlagRolloutType =
  (typeof FeatureFlagRolloutType)[keyof typeof FeatureFlagRolloutType];

export interface FeatureFlagVariant {
  key: string;
  /** Relative weight for weighted-random selection. At least one must be > 0. */
  weight: number;
}

export interface FeatureFlag {
  id: string;
  key: string;
  description?: string | null;
  type: FeatureFlagType;
  scope: FeatureFlagScope;
  /** Solution slug or merchant id. Always '' for GLOBAL — the server forces it. */
  scopeValue: string;
  enabled: boolean;
  rolloutType: FeatureFlagRolloutType;
  rolloutPercentage?: number | null;
  allowListMerchantIds: string[];
  variants?: FeatureFlagVariant[] | null;
  createdById: string;
  updatedById?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FeatureFlagAuditEntry {
  id: string;
  actorId: string;
  actorEmail?: string | null;
  action: string;
  resource: string;
  resourceId: string;
  payload?: { before?: unknown; after?: unknown } | null;
  createdAt: string;
}

export interface FeatureFlagQuery {
  key?: string;
  scope?: FeatureFlagScope;
  enabled?: boolean;
}

/**
 * Create carries the immutable fields (`key`, `type`, `scope`, `scopeValue`);
 * update carries only what the server's UpdateFeatureFlagDto accepts. Splitting
 * them keeps the form from offering edits the backend silently ignores — the
 * unique index is (key, scope, scopeValue), so those three are identity.
 */
export interface CreateFeatureFlagPayload {
  key: string;
  description?: string;
  type?: FeatureFlagType;
  scope?: FeatureFlagScope;
  scopeValue?: string;
  enabled?: boolean;
  rolloutType?: FeatureFlagRolloutType;
  rolloutPercentage?: number;
  allowListMerchantIds?: string[];
  variants?: FeatureFlagVariant[];
}

export interface UpdateFeatureFlagPayload {
  description?: string;
  enabled?: boolean;
  rolloutType?: FeatureFlagRolloutType;
  rolloutPercentage?: number;
  allowListMerchantIds?: string[];
  variants?: FeatureFlagVariant[];
}
